import { createApp, createSSRApp, h } from 'vue';
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router';
import createStore from './common/store/index.js';
import initialFilters from './common/filters/index.js';
import initializeRouter from './common/routers/index.js';
import $eventBus from './common/providers/eventbus.js';
import urlMixin from './common/mixins/url.js';
import vnumberMixin from './common/mixins/vnumber.js';
import compNameMixin from './common/mixins/comp.js';
import molang from './common/plugins/molang.js';
import App from './app.vue';
import { loadDebugBear } from '@/common/utilities/nojq/_load.thirdparty.scripts.js';

const isSSR = typeof window === 'undefined';

export function importDatadogRUM() {
    return import(/* webpackChunkName: "datadog-rum" */ '@datadog/browser-rum');
}

// CW-4883: which sessions record is decided by the split's targeting rules on the
// session-persisted landing_url attribute (e.g. contains utm_campaign=avm_update),
// so no campaign is hardcoded here.
function initSessionReplay(glb) {
    if (!glb || !glb.enableThirdPart || glb.splits?.['movoto-session-replay-CW-4883'] !== 'on') {
        return;
    }
    importDatadogRUM()
        .then(({ datadogRum }) => {
            datadogRum.init({
                applicationId: 'a5fff77c-4a9e-4f31-8b13-2917d5c2d937',
                clientToken: 'pub0d7b506a8f86075d8db32685ed779e03',
                site: 'datadoghq.com',
                service: 'movoto-web',
                env: glb.isTenX ? glb.tenXEnv : glb.isDevelopment ? 'development' : 'production',
                version: glb.version,
                sessionSampleRate: 100,
                sessionReplaySampleRate: 100,
                defaultPrivacyLevel: 'mask-user-input',
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                trackViewsManually: true, // views start in router.beforeEach (vweb/common/routers/index.js) only on real path changes, so query-param churn doesn't spawn views
            });
            const user = { movotoDeviceId: glb.movotoDeviceID };
            if (glb.user && glb.user.id) {
                user.id = glb.user.id;
                user.email = glb.user.email;
            }
            datadogRum.setUser(user);
        })
        .catch((err) => {
            console.error('An error occured initializing Datadog RUM session replay', err);
        });
}

const getRouter = (routes, defaultPath) =>
    createRouter({
        history: (isSSR ? createMemoryHistory : createWebHistory)(defaultPath),
        routes,
    });

const createVueApp = (model = null, appCreator) => {
    if (!isSSR) {
        const glb = window.__INITIAL_STATE__;
        if (glb && glb.enableThirdPart && glb.splits['movoto-enable-debugbear-CW-4406'] === 'on') {
            loadDebugBear();
        }
        initSessionReplay(glb);
    }

    const store = createStore();
    const router = initializeRouter(getRouter, model);

    const app = appCreator({
        router,
        render: () => h(App),
    });

    app.provide('$eventBus', $eventBus);
    app.use(router);
    app.use(store);

    molang.install(app, store);
    initialFilters.install(app);
    app.config.globalProperties.isSSR = isSSR;

    app.mixin(compNameMixin);
    app.mixin(urlMixin);
    app.mixin(vnumberMixin);
    // https://stackoverflow.com/a/64429013
    app.mixin({
        mounted() {
            this.$el.__vue__ = this; // for __vue__ for backwards compatibility.
        },
    });

    return { app, router, store };
};

export function createSSR(model) {
    let app = createVueApp(model, createSSRApp);
    return app;
}
export function createCSR(model) {
    let app = createVueApp(model, createApp);
    return app;
}
